<script lang="ts">
    import { getContext, onDestroy, tick } from 'svelte'
    import { derived as sderived } from 'svelte/store'
    import type { Readable } from 'svelte/store'

    import Icon from '@iconify/svelte'
    import type { DateTime } from 'luxon'

    import { gettext as _ } from 'hydria/services/gettext'
    import { notify } from 'hydria/services/notifications'
    import { simpleFetch } from 'hydria/services/session'
    import { sanchor, spath } from 'hydria/svelte_helpers'
    import { toLuxon } from 'hydria/utils/time'

    import { TOP_PADDING } from 'wdom/constants'

    import type { User, UserHandler } from './user'

    import Date from 'hydria/components/Date.svelte'
    import LoadingPattern from 'hydria/components/LoadingPattern.svelte'

    const userh = getContext('User') as UserHandler

    const unsubs: any[] = []
    onDestroy(() => {
        for (const unsub of unsubs) unsub.unsubscribe != null ? unsub.unsubscribe() : unsub()
    })

    const user: Readable<User> = sderived(userh.userWritable, ($v) => $v)

    interface LogEntry {
        id: string
        user_id: string
        revision: number
        stamp: DateTime
        action: 'create' | 'update'
        comment: string
        revision_source: {
            ip?: string
            user_id?: string
        }
    }

    let panelOpened = $state(false)
    let panelElement: HTMLElement | undefined = $state()
    function togglePanel() {
        panelOpened = !panelOpened

        if (panelOpened) {
            scrollToMe()
        }
    }

    function scrollToMe() {
        tick().then(() => {
            if (panelElement) {
                window.scrollTo({
                    top: panelElement.offsetTop - TOP_PADDING,
                    // top: document.getElementById('advanced').offsetTop - TOP_PADDING,
                    left: 0,
                    behavior: 'smooth',
                })
            }
        })
    }

    let logStatus: 'ready' | 'loading' | 'error' | null = $state(null)
    let entries: LogEntry[] = $state([])
    let userMapping: {
        [key: string]: {
            name?: string
            org?: string
        }
    } = $state({})
    let adminMapping: { [key: string]: string | null } = $state({})

    async function loadLog(recenter?: boolean) {
        logStatus = 'loading'

        try {
            const r = await simpleFetch('GET', `/json/wdom-user/1/${$user.id}/log`)
            for (const entry of r.data.items) {
                if (entry.stamp) entry.stamp = toLuxon(entry.stamp)
            }

            entries = r.data.items
            userMapping = r.data.user_mapping
            adminMapping = r.data.admin_mapping

            logStatus = 'ready'
            if (recenter) scrollToMe()
        } catch (e) {
            console.error('Error loading log:', e)
            notify({
                type: 'danger',
                title: 'Journal de modifications',
                text: 'Le journal n’a pas pu être chargé.',
            })
            logStatus = 'error'
        }
    }

    $effect(() => {
        if (logStatus == null && panelOpened) loadLog(true)
    })
    unsubs.push(
        user.subscribe(($user) => {
            if (logStatus == 'error' || logStatus == 'ready') {
                if (panelOpened) loadLog(false)
                else logStatus = null
            }
        }),
    )
</script>

<div class="card" bind:this={panelElement}>
    <div class="card-title text-primary" aria-hidden="true" onclick={() => togglePanel()} style="margin-bottom: calc(var(--bs-card-title-spacer-top) + var(--bs-card-spacer-y));">
        <span class="col">{$_('User modification log')}</span>
        <span class="icon-collapsed">
            {#if panelOpened}
                <Icon inline icon="fa:chevron-down" />
            {:else}
                <Icon inline icon="fa:chevron-right" />
            {/if}
        </span>
    </div>
    {#if panelOpened}
        {#if logStatus == 'loading'}
            <div class="card-body pt-0">
                <LoadingPattern pattern={['x', 'x']} />
            </div>
        {:else if logStatus == 'error'}
            <div class="card-body pt-0">
                <p class="text-error">L’information n’a pas pu être chargée.</p>
            </div>
        {:else if logStatus == 'ready'}
            <div class="card-body pt-0">
                <table class="table collapsed-table u-mod-log mb-0">
                    <thead>
                        <tr>
                            <th>{$_('Stamp')}</th>
                            <th>{$_('Action')}</th>
                            <th>{$_('Comment')}</th>
                            <th>{$_('Changements')}</th>
                            <th>{$_('IP')}</th>
                            <th>{$_('User')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {#if entries.length < 1}
                            <tr><td colspan="6">Aucune entrée</td></tr>
                        {:else}
                            {#each entries as entry}
                                <tr>
                                    <td><Date value={entry.stamp} format="h-table-s" /></td>
                                    <td>{entry.action}</td>
                                    <td>{entry.comment}</td>
                                    <td>
                                        {#if entry.user_fields && Object.keys(entry.user_fields).length}
                                            <div><em>u</em>: {JSON.stringify(entry.user_fields)}</div>
                                        {/if}
                                        {#if entry.user_metadata_fields && Object.keys(entry.user_metadata_fields).length}
                                            <div><em>meta</em>: {JSON.stringify(entry.user_metadata_fields)}</div>
                                        {/if}
                                    </td>
                                    <td>{entry.revision_source && entry.revision_source.ip ? entry.revision_source.ip : '–'}</td>
                                    <td>
                                        {#if entry.revision_source && entry.revision_source.user_id}
                                            {#if entry.revision_source.user_id === entry.user_id}
                                                Compte utilisateur
                                            {:else if adminMapping[entry.revision_source.user_id] !== undefined}
                                                <a use:sanchor href={spath(`/user/${entry.revision_source.user_id}`)}>{adminMapping[entry.revision_source.user_id] != null ? adminMapping[entry.revision_source.user_id] : entry.revision_source.user_id}</a>
                                                <span class="text-muted">(admin)</span>
                                            {:else if userMapping[entry.revision_source.user_id] !== undefined}
                                                <a use:sanchor href={spath(`/wdom-user/${entry.revision_source.user_id}`)}>
                                                    {#if userMapping[entry.revision_source.user_id] == null && (userMapping[entry.revision_source.user_id].org || userMapping[entry.revision_source.user_id].name)}
                                                        {entry.revision_source.user_id}
                                                    {:else if userMapping[entry.revision_source.user_id].org}
                                                        {userMapping[entry.revision_source.user_id].name ? userMapping[entry.revision_source.user_id].name : '?'} ({userMapping[entry.revision_source.user_id].org})
                                                    {:else}
                                                        {userMapping[entry.revision_source.user_id].name}
                                                    {/if}
                                                </a>
                                                <span class="text-muted">(user)</span>
                                            {:else}
                                                <span class="text-danger">Aucune information</span>
                                            {/if}
                                        {/if}
                                    </td>
                                </tr>
                            {/each}
                        {/if}
                    </tbody>
                </table>
            </div>
        {/if}
    {/if}
</div>
